'use client'

import {
  IconAlertCircleYellow,
  IconArrowLeft,
} from '@macarongfactory/web-icons'
import { Result, SolidButton } from '@macarongfactory/web-ui'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState, useTransition } from 'react'

import { COMMON_ERROR_DESCRIPTION, COMMON_ERROR_TITLE } from '@/shared/lib'

import ModoocarSkeleton from './ModoocarSkeleton'
import { useIsInApp } from '../_features/modoocar'

const ALERT_ICON_SIZE_PX = 56
const APP_BAR_HEIGHT_PX = 56
const BACK_ICON_SIZE_PX = 24
const BACK_BUTTON_SIZE_PX = 44

const RETRY_BUTTON_TEXT = '다시 시도하기'
const SKELETON_MIN_DURATION_MS = 1000

const ModoocarLoadError = () => {
  const isInApp = useIsInApp()
  const router = useRouter()

  const [isRetrying, startRetry] = useTransition()

  const [isMinDurationActive, setIsMinDurationActive] = useState(false)
  const minDurationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // [주의]: 아래에서 주소를 홈으로 바꾸므로 원래 상세 URL을 저장해 둡니다. 저장하지 않으면 다시 시도할 때 홈을 다시 불러옵니다.
  const originalUrlRef = useRef<string | null>(null)

  // [주의]: 404 URL을 히스토리에서 홈으로 바꿉니다. 바꾸지 않으면 뒤로 갔다가 다시 들어올 때 캐시된 404를 다시 만납니다.
  useEffect(() => {
    if (originalUrlRef.current === null) {
      originalUrlRef.current = window.location.href
    }
    window.history.replaceState(null, '', '/v2/modoocar/home')
  }, [])

  useEffect(() => {
    return () => {
      if (minDurationTimerRef.current) {
        clearTimeout(minDurationTimerRef.current)
      }
    }
  }, [])

  // [앱]: reload()나 location.replace()를 쓰면 안드로이드 인앱 웹뷰에서 하단 네비게이션이 사라지거나 새 창이 열립니다.
  // 그래서 주소를 원래 상세 URL로 되돌린 뒤 router.refresh()로 RSC만 다시 요청합니다.
  const handleRetry = () => {
    if (originalUrlRef.current) {
      window.history.replaceState(null, '', originalUrlRef.current)
    }
    setIsMinDurationActive(true)
    if (minDurationTimerRef.current) {
      clearTimeout(minDurationTimerRef.current)
    }
    minDurationTimerRef.current = setTimeout(() => {
      setIsMinDurationActive(false)
    }, SKELETON_MIN_DURATION_MS)
    startRetry(() => {
      router.refresh()
    })
  }

  // [앱]: 웹뷰에서는 router.back()이 히스토리 상태에 따라 불안정해서 진입점인 홈으로 직접 이동합니다.
  const handleBack = () => {
    router.push('/modoocar/home')
  }

  if (isRetrying || isMinDurationActive) {
    return <ModoocarSkeleton />
  }

  return (
    <div className='flex min-h-dvh w-full flex-col bg-background-surface-default'>
      {isInApp ? (
        <header
          className='flex shrink-0 items-center px-dimension-x1 py-[6px]'
          style={{ height: APP_BAR_HEIGHT_PX }}
        >
          <button
            type='button'
            aria-label='이전 화면으로 이동'
            onClick={handleBack}
            className='flex items-center justify-center'
            style={{ width: BACK_BUTTON_SIZE_PX, height: BACK_BUTTON_SIZE_PX }}
          >
            <IconArrowLeft
              width={BACK_ICON_SIZE_PX}
              height={BACK_ICON_SIZE_PX}
              className='text-foreground-neutral-strong'
            />
          </button>
        </header>
      ) : null}

      <div className='flex flex-1 items-center justify-center'>
        <Result
          top={
            <Result.Top.Image>
              <IconAlertCircleYellow
                width={ALERT_ICON_SIZE_PX}
                height={ALERT_ICON_SIZE_PX}
              />
            </Result.Top.Image>
          }
          title={<Result.Title text={COMMON_ERROR_TITLE} />}
          description={<Result.Description text={COMMON_ERROR_DESCRIPTION} />}
          button={
            <SolidButton
              variant='neutral-weak'
              text={RETRY_BUTTON_TEXT}
              size='medium'
              clickable={handleRetry}
            />
          }
        />
      </div>
    </div>
  )
}

export default ModoocarLoadError
